import React, { useState } from 'react';
import { SlidersHorizontal, X, Search } from 'lucide-react';
import { cn } from '../utils/cn';
import { ILMSButton } from './ILMSButton';
import { ILMSModal } from './ILMSModal';

export interface ILMSFilterBarProps {
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  children?: React.ReactNode; // filter controls (selects, date pickers...)
  activeCount?: number;
  onClear?: () => void;
  className?: string;
}

/** Horizontal filter row on desktop/tablet; collapses into a "Filters" drawer button on mobile. */
export function ILMSFilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search…',
  children,
  activeCount = 0,
  onClear,
  className,
}: ILMSFilterBarProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex items-center gap-2">
        {onSearchChange && (
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" aria-hidden="true" />
            <input
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-11 min-h-touch w-full rounded-md border border-ink-300/50 bg-white pl-9 pr-3 text-body focus-visible:outline-2 focus-visible:outline-brand-green-500"
            />
          </div>
        )}
        {children && (
          <>
            <div className="hidden lg:flex items-center gap-2 flex-wrap">{children}</div>
            <ILMSButton
              variant="outline"
              size="md"
              icon={<SlidersHorizontal />}
              className="lg:hidden shrink-0"
              onClick={() => setDrawerOpen(true)}
            >
              Filters{activeCount > 0 ? ` (${activeCount})` : ''}
            </ILMSButton>
          </>
        )}
        {activeCount > 0 && onClear && (
          <button onClick={onClear} className="hidden lg:inline-flex items-center gap-1 text-secondary text-brand-green-700 hover:underline shrink-0">
            <X className="h-3.5 w-3.5" /> Clear filters
          </button>
        )}
      </div>

      {children && (
        <ILMSModal open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Filters" size="sm"
          footer={
            <>
              {onClear && (
                <ILMSButton variant="ghost" onClick={() => { onClear(); setDrawerOpen(false); }}>
                  Clear filters
                </ILMSButton>
              )}
              <ILMSButton onClick={() => setDrawerOpen(false)}>Apply</ILMSButton>
            </>
          }
        >
          <div className="flex flex-col gap-3">{children}</div>
        </ILMSModal>
      )}
    </div>
  );
}
